import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/common/Layout';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { rrhhInventarioService } from '../services/rrhhInventario';
import { ubicacionesService } from '../services/ubicaciones';
import toast from 'react-hot-toast';
import { Users, Plus, Search, Edit, X, Upload, Download } from 'lucide-react';

// ─── Modal: nuevo / editar empleado ──────────────────────────────────────────

const ModalEmpleado = ({ empleado, ubicaciones, onCerrar, onGuardado }) => {
    const esEdicion = !!empleado;
    const [nombre, setNombre] = useState(empleado?.nombre_completo || '');
    const [documento, setDocumento] = useState(empleado?.documento || '');
    const [cargo, setCargo] = useState(empleado?.cargo || '');
    const [unidadNegocio, setUnidadNegocio] = useState(empleado?.unidad_negocio || '');
    const [fechaIngreso, setFechaIngreso] = useState(empleado?.fecha_ingreso || '');
    const [estadoColaborador, setEstadoColaborador] = useState(empleado?.estado_colaborador || '');
    const [ubicacionId, setUbicacionId] = useState(empleado?.ubicacion_id || '');
    const [activo, setActivo] = useState(empleado?.activo ?? true);
    const [guardando, setGuardando] = useState(false);

    const handleGuardar = async () => {
        if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
        const payload = {
            nombre_completo: nombre.trim(),
            documento: documento.trim() || null,
            cargo: cargo.trim() || null,
            unidad_negocio: unidadNegocio.trim() || null,
            fecha_ingreso: fechaIngreso || null,
            estado_colaborador: estadoColaborador.trim() || null,
            ubicacion_id: ubicacionId ? Number(ubicacionId) : null,
            activo,
        };
        setGuardando(true);
        try {
            if (esEdicion) {
                await rrhhInventarioService.actualizarEmpleado(empleado.id, payload);
                toast.success('Empleado actualizado');
            } else {
                await rrhhInventarioService.crearEmpleado(payload);
                toast.success('Empleado creado');
            }
            onGuardado();
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al guardar el empleado');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal title={esEdicion ? 'Editar empleado' : 'Nuevo empleado'} onClose={onCerrar} size="sm">
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre completo</label>
                    <input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">DNI / Documento (opcional)</label>
                    <input
                        type="text"
                        value={documento}
                        onChange={(e) => setDocumento(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Cargo / Posición (opcional)</label>
                    <input
                        type="text"
                        value={cargo}
                        onChange={(e) => setCargo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Unidad de negocio (opcional)</label>
                    <input
                        type="text"
                        value={unidadNegocio}
                        onChange={(e) => setUnidadNegocio(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Fecha de ingreso (opcional)</label>
                        <input
                            type="date"
                            value={fechaIngreso}
                            onChange={(e) => setFechaIngreso(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Estado (opcional)</label>
                        <input
                            type="text"
                            value={estadoColaborador}
                            onChange={(e) => setEstadoColaborador(e.target.value)}
                            placeholder="Ej: Activo"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Sede / Ubicación (opcional)</label>
                    <select
                        value={ubicacionId}
                        onChange={(e) => setUbicacionId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="">Sin asignar</option>
                        {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                    </select>
                </div>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
                    <span>Colaborador activo</span>
                </label>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancelar
                </button>
                <Button onClick={handleGuardar} loading={guardando} disabled={guardando}>
                    Guardar
                </Button>
            </div>
        </Modal>
    );
};

// ─── Modal: cargar Excel ─────────────────────────────────────────────────────

const ModalCargarExcel = ({ onCerrar, onCargado }) => {
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleSubir = async () => {
        if (!archivo) { toast.error('Selecciona un archivo Excel'); return; }
        setSubiendo(true);
        try {
            const res = await rrhhInventarioService.cargarExcelEmpleados(archivo);
            setResultado(res);
            toast.success(res.message || 'Archivo procesado');
            onCargado();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al procesar el archivo');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <Modal title="Cargar empleados (Excel)" onClose={onCerrar} size="sm">
            <div className="space-y-4">
                <button
                    onClick={() => rrhhInventarioService.descargarPlantillaEmpleados()}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                    <Download className="h-4 w-4 mr-1" /> Descargar plantilla de ejemplo
                </button>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Archivo Excel</label>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        Acepta el archivo tal cual lo exporta RRHH (Nombre completo, Documento, Nombre de Posición,
                        Unidad de negocio, Fecha de entrada en la empresa, Estado de colaborador, Sede) — no hace
                        falta renombrar columnas.
                    </p>
                </div>
                {resultado && (
                    <div className="text-sm bg-gray-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-700">Creados: {resultado.creados}</p>
                        {resultado.errores?.length > 0 && (
                            <ul className="mt-2 text-xs text-red-600 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                                {resultado.errores.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
                <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cerrar
                </button>
                <Button onClick={handleSubir} loading={subiendo} disabled={subiendo || !archivo}>
                    <Upload className="h-4 w-4 inline mr-1" /> Subir
                </Button>
            </div>
        </Modal>
    );
};

// ─── Página principal ───────────────────────────────────────────────────────

const RRHHEmpleadosPage = () => {
    const [empleados, setEmpleados] = useState([]);
    const [ubicaciones, setUbicaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [mostrarInactivos, setMostrarInactivos] = useState(false);
    const [modalEmpleado, setModalEmpleado] = useState(null);
    const [modalExcelAbierto, setModalExcelAbierto] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [emps, ubis] = await Promise.all([
                rrhhInventarioService.listarEmpleados(busqueda || null, !mostrarInactivos),
                ubicacionesService.getUbicacionesSelect(true),
            ]);
            setEmpleados(emps);
            setUbicaciones(ubis);
        } catch (err) {
            toast.error('Error al cargar los empleados');
        } finally {
            setLoading(false);
        }
    }, [busqueda, mostrarInactivos]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleEliminar = async (id) => {
        if (!window.confirm('¿Desactivar este empleado?')) return;
        try {
            await rrhhInventarioService.eliminarEmpleado(id);
            toast.success('Empleado desactivado');
            cargar();
        } catch (err) {
            toast.error('Error al desactivar el empleado');
        }
    };

    return (
        <Layout>
            <div className="p-6 max-w-5xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 p-2.5 rounded-xl">
                            <Users className="h-6 w-6 text-blue-700" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">RRHH · Empleados</h1>
                            <p className="text-sm text-gray-500">Directorio del personal que recibe uniformes/equipos</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setModalExcelAbierto(true)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            <Upload className="h-4 w-4 mr-2" /> Cargar Excel
                        </button>
                        <Button onClick={() => setModalEmpleado({})}>
                            <Plus className="h-4 w-4 inline mr-1" /> Nuevo empleado
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="relative max-w-sm flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por nombre..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                    <label className="flex items-center space-x-2 text-sm text-gray-600">
                        <input type="checkbox" checked={mostrarInactivos} onChange={(e) => setMostrarInactivos(e.target.checked)} />
                        <span>Mostrar inactivos/cesados</span>
                    </label>
                </div>

                {loading ? (
                    <Loading message="Cargando empleados..." />
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Nombre</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Documento</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Cargo</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Unidad de negocio</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Ingreso</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Sede</th>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {empleados.length === 0 && (
                                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Sin empleados registrados</td></tr>
                                )}
                                {empleados.map((e) => (
                                    <tr key={e.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 font-medium text-gray-900">{e.nombre_completo}</td>
                                        <td className="px-4 py-3 text-gray-500">{e.documento || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600">{e.cargo || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600">{e.unidad_negocio || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{e.fecha_ingreso || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600">{e.ubicacion_nombre || '—'}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                e.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {e.estado_colaborador || (e.activo ? 'Activo' : 'Inactivo')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => setModalEmpleado(e)} className="text-gray-400 hover:text-blue-600 mr-2" title="Editar">
                                                <Edit className="h-4 w-4 inline" />
                                            </button>
                                            <button onClick={() => handleEliminar(e.id)} className="text-gray-400 hover:text-red-600" title="Desactivar">
                                                <X className="h-4 w-4 inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modalEmpleado !== null && (
                <ModalEmpleado
                    empleado={modalEmpleado.id ? modalEmpleado : null}
                    ubicaciones={ubicaciones}
                    onCerrar={() => setModalEmpleado(null)}
                    onGuardado={cargar}
                />
            )}
            {modalExcelAbierto && (
                <ModalCargarExcel onCerrar={() => setModalExcelAbierto(false)} onCargado={cargar} />
            )}
        </Layout>
    );
};

export default RRHHEmpleadosPage;
